'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type PopStoreExtendedSubscription,
  PopStoreExtendedSubscriptionLevel,
  PopStoreSubscriptionLevel,
} from '@ps/models/lib/creator-subscription';

import { SubscriptionConfigApiError } from '../functions/subscriptionApis';
import {
  type SubscriptionConfigurationFormResult,
  kDefaultCommitmentPeriod,
} from '../hooks/useSubscriptionConfigurationForm';

/** 1:1 with legacy `useSubscriptionErrorHandler`: a 422 from the
 *  creator service carries a validation reason (e.g. the price
 *  can't be changed again until the current billing cycle ends /
 *  one month since the last change) plus, when applicable, the
 *  epoch-seconds timestamp the creator can next change it at. */
function subscriptionSaveErrorMessage(err: unknown): string {
  if (err instanceof SubscriptionConfigApiError) {
    if (err.nextAllowedActivationDate == null) return err.message;
    const nextAllowedDate = new Date(
      err.nextAllowedActivationDate * 1000,
    ).toLocaleString();
    return `${err.message} (${nextAllowedDate})`;
  }
  if (err instanceof Error) return err.message;
  return 'Failed to save subscription.';
}

interface Props {
  /** Form instance owned by the parent `SubscriptionTierRow` so
   *  the header (Enable + inline title) and body share one form. */
  form: SubscriptionConfigurationFormResult;
  extendedSubscription: PopStoreExtendedSubscription;
  minSubscriptionPrice?: number;
  maxSubscriptionPrice?: number;
  onSaved?: () => void;
  onError?: (message: string) => void;
}

/**
 * Per-tier configuration form rendered inside an opened
 * `SubscriptionTierRow`. 1:1 with the BODY half of legacy
 * `SubscriptionSetupField` (the `Header` half — active toggle
 * + inline title edit — lives in `SubscriptionTierRow`).
 *
 * Body layout (top → bottom):
 *   1. Set Subscription Price (USD) — paid tiers only.
 *   2. 6 Mo. Prepay Discount — VIP only, optional. Typing a
 *      number auto-flips `isLongerCommitment`.
 *   3. What users will get — required textarea (every tier).
 *   4. Save button — disabled while invalid or saving.
 *
 * Banner / preview-video uploads, active toggle, and tier-name
 * field are NOT here — they're either in the header
 * (`SubscriptionTierRow`) or not part of the legacy flow.
 */
export function SubscriptionSetupField({
  form,
  extendedSubscription,
  minSubscriptionPrice,
  maxSubscriptionPrice,
  onSaved,
  onError,
}: Props) {
  const [spinning, setSpinning] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [justSaved, setJustSaved] = useState(false);
  const justSavedTimeoutRef = useRef<number | undefined>(undefined);
  const saveErrorTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(justSavedTimeoutRef.current);
      window.clearTimeout(saveErrorTimeoutRef.current);
    },
    [],
  );

  const isFollower =
    extendedSubscription.level === PopStoreExtendedSubscriptionLevel.FOLLOWER;
  const isVIP =
    !isFollower &&
    extendedSubscription.subscription.platform_sub_level ===
      PopStoreSubscriptionLevel.VIP;

  const handleSave = useCallback(async () => {
    // 1:1 with legacy `onSave`: re-validate up front; if invalid,
    // surface a price-specific alert via `onError` (the modal-
    // level toast — legacy used `useAlert`).
    const errs = form.validate();
    const isValidNow = Object.values(errs).every(m => !m);
    if (!isValidNow) {
      const p = form.value.monthlyPrice;
      if (
        minSubscriptionPrice != null &&
        p != null &&
        p < minSubscriptionPrice
      ) {
        onError?.(
          `Subscription price can't be less than $${minSubscriptionPrice.toFixed(
            2,
          )}.`,
        );
      } else if (
        maxSubscriptionPrice != null &&
        p != null &&
        p > maxSubscriptionPrice
      ) {
        onError?.(
          `Subscription price can't exceed $${maxSubscriptionPrice.toFixed(
            2,
          )}. It must be lower than the next tier.`,
        );
      }
      return;
    }

    setSaveError(undefined);
    setSpinning(true);
    try {
      await form.save();
      onSaved?.();
      setJustSaved(true);
      justSavedTimeoutRef.current = window.setTimeout(
        () => setJustSaved(false),
        3000,
      );
    } catch (err) {
      console.error('Failed to save subscription configuration:', err);
      setSaveError(subscriptionSaveErrorMessage(err));
      saveErrorTimeoutRef.current = window.setTimeout(
        () => setSaveError(undefined),
        5000,
      );
    } finally {
      setSpinning(false);
    }
  }, [form, onSaved, minSubscriptionPrice, maxSubscriptionPrice]);

  // 6-month discounted total. 1:1 with legacy
  // `longerCommitmentDiscountedPrice`:
  //   • Period is the constant `kDefaultCommitmentPeriod` (6),
  //     NOT the form's `commitment` field.
  //   • Returns '0.00' when VIP gate / price / discount is missing
  //     or the discount is out of 1–100 range.
  const longerCommitmentDiscountedPrice = (() => {
    if (!isVIP || !form.value.commitmentDiscount || !form.value.monthlyPrice) {
      return '0.00';
    }
    const discountValue = form.value.commitmentDiscount;
    const pricePerMonth = form.value.monthlyPrice;
    const isValidDiscount =
      !Number.isNaN(discountValue) &&
      discountValue >= 1 &&
      discountValue <= 100;
    const isValidPrice = !Number.isNaN(pricePerMonth);
    if (!isValidDiscount || !isValidPrice) return '0.00';
    const totalBeforeDiscount =
      Number(kDefaultCommitmentPeriod) * pricePerMonth;
    return (totalBeforeDiscount * (1 - discountValue / 100)).toFixed(2);
  })();

  return (
    <div className="flex flex-col gap-3">
      {/* Set Subscription Price (USD) — paid tiers only.
          Legacy layout: label on the left (50% width), `$` input
          + " / month" right-aligned. */}
      {!isFollower ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-content-1 text-sub flex w-fit flex-col font-normal leading-snug tracking-normal">
              <span>Set Subscription Price</span>
              <span className="text-center">(price in USD)</span>
            </span>
            <div className="flex shrink-0 items-center gap-1.5">
              <div className="focus-within:border-action border-raise/10 bg-raise/[0.06] flex h-9 w-[90px] items-center rounded-lg border sm:w-[120px]">
                <span className="text-raise/60 text-sub pl-2.5 font-normal tracking-normal">
                  $
                </span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  value={form.value.monthlyPrice ?? ''}
                  onChange={e =>
                    form.setValue(
                      'monthlyPrice',
                      e.target.value === ''
                        ? undefined
                        : Number(e.target.value),
                    )
                  }
                  onWheel={e => e.currentTarget.blur()}
                  placeholder={minSubscriptionPrice?.toFixed(2) ?? '0.00'}
                  disabled={spinning}
                  className="text-content-1 placeholder:text-raise/40 h-full w-full bg-transparent px-2 text-center text-base [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
              </div>
              <span className="text-content-1 text-sub font-normal tracking-normal">
                / month
              </span>
            </div>
          </div>
          {form.errors.monthlyPrice ? (
            <span className="text-fine text-danger text-right">
              {form.errors.monthlyPrice}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* 6 Mo. Prepay Discount — VIP only, always shown when the
          tier is open. Typing a number flips `isLongerCommitment`
          on; clearing it flips it off. 1:1 with legacy
          `setCommitmentDiscount` auto-coupling — no separate UI
          toggle. */}
      {isVIP ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-content-1 text-sub flex-1 font-normal leading-snug tracking-normal">
              6 Mo. Prepay Discount (optional)
            </span>
            <div className="flex shrink-0 items-center gap-1.5">
              <input
                type="number"
                step="1"
                min="1"
                max="100"
                inputMode="numeric"
                value={form.value.commitmentDiscount ?? ''}
                onChange={e =>
                  form.setCommitmentDiscount(
                    e.target.value === '' ? undefined : Number(e.target.value),
                  )
                }
                onWheel={e => e.currentTarget.blur()}
                placeholder="25"
                disabled={spinning}
                className="focus:border-action border-raise/10 bg-raise/[0.06] text-content-1 placeholder:text-raise/40 h-9 w-[60px] rounded-lg border px-2 text-center text-base [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <span className="text-content-1 text-sub font-normal tracking-normal">
                % = ${longerCommitmentDiscountedPrice} / 6 mo.
              </span>
            </div>
          </div>
          {form.errors.commitmentDiscount ? (
            <span className="text-fine text-danger text-right">
              {form.errors.commitmentDiscount}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* What users will get — required textarea (every tier). */}
      <div className="flex flex-col gap-1">
        <label className="text-raise/60 text-fine">
          What subscribers will get <span className="text-danger">*</span>
        </label>
        <textarea
          value={form.value.description}
          onChange={e => form.setValue('description', e.target.value)}
          rows={4}
          maxLength={160}
          placeholder="Let people know what they'll get here (max 160 characters)"
          disabled={spinning}
          className="focus:border-action border-raise/10 bg-raise/5 text-content-1 placeholder:text-raise/40 min-h-[96px] rounded-lg border px-3 py-2 text-base focus:outline-none"
        />
        {form.errors.description ? (
          <span className="text-fine text-danger">
            {form.errors.description}
          </span>
        ) : null}
      </div>

      {/* Save — disabled while invalid or in-flight. */}
      <button
        type="button"
        onClick={handleSave}
        disabled={!form.isValid || spinning}
        className="bg-brand text-content-on-brand text-body mt-3 flex h-10 items-center justify-center rounded-lg transition disabled:opacity-50"
      >
        {spinning ? 'Saving…' : 'Save'}
      </button>

      {saveError ? (
        <div className="bg-danger-soft border-danger/40 text-fine text-danger rounded-lg border px-3 py-2">
          {saveError}
        </div>
      ) : null}

      {justSaved ? (
        <div className="bg-brand/15 border-brand/40 text-brand-text text-fine rounded-lg border px-3 py-2 text-center">
          Tier saved.
        </div>
      ) : null}
    </div>
  );
}
